import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CoachTicketService } from '../../core/services/coach-ticket.service';
import { ICoachTicket, ICoachTicketMessage, ICoachTicketTag } from '../../core/models/ticket.model';
import { ECoachTicketStatus } from '../../core/enums/ticket-status.enum';
import { ECoachTicketUrgency } from '../../core/enums/ticket-urgency.enum';
import { ECoachTicketSenderType } from '../../core/enums/ticket-sender-type.enum';

const PAGE_SIZE = 20;

@Component({
  selector: 'coach-tickets',
  templateUrl: './tickets.component.html',
  styleUrl: './tickets.component.scss',
})
export class TicketsComponent implements OnInit, OnDestroy {

  tickets: ICoachTicket[] = [];
  /** Only one thread is expanded at a time; its messages are fetched on demand. */
  openTicketId?: number;
  openTicket?: ICoachTicket;

  statusFilter?: ECoachTicketStatus;
  urgencyFilter?: ECoachTicketUrgency;
  /** Tags to narrow the list by; a ticket matches when it carries any of them. */
  tagFilterIds: number[] = [];
  searchText = '';

  page = 1;
  hasMore = false;
  waitingReplyCount = 0;

  formIsOpen = false;
  fg: FormGroup = this.formBuilder.group({
    title: [null, [Validators.required, Validators.maxLength(255)]],
    urgency: [ECoachTicketUrgency.Medium, [Validators.required]],
    message: [null, [Validators.required]],
    // The API refuses an untagged ticket: the admin list is routed by these.
    tagIds: [[] as number[], [Validators.required]],
  });

  fgReply: FormGroup = this.formBuilder.group({
    body: [null, [Validators.required]],
  });

  urgencyOptions: { id: number, name: string }[] = [];
  tags: ICoachTicketTag[] = [];
  statusTabs: { value?: ECoachTicketStatus, labelKey: string }[] = [
    {value: undefined, labelKey: 'tickets.filter_all'},
    {value: ECoachTicketStatus.WaitingReply, labelKey: 'tickets.status_waiting'},
    {value: ECoachTicketStatus.Answered, labelKey: 'tickets.status_answered'},
    {value: ECoachTicketStatus.Closed, labelKey: 'tickets.status_closed'},
  ];

  ticketStatus = ECoachTicketStatus;
  ticketUrgency = ECoachTicketUrgency;
  senderType = ECoachTicketSenderType;

  isLoading = false;
  isSubmitted = false;
  createIsLoading = false;
  replyIsLoading = false;
  replyIsSubmitted = false;
  detailIsLoading = false;

  destroyed$ = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private ticketService: CoachTicketService,
    private toastrService: ToastrService,
    private translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.buildUrgencyOptions();
    this.getTags();
    this.getTickets();
    this.getWaitingCount();
  }

  getTags() {
    this.ticketService.getTags()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => this.tags = (r.data?.result ?? r.data ?? []) as ICoachTicketTag[]);
  }

  buildUrgencyOptions() {
    const keys = ['tickets.urgency_low', 'tickets.urgency_medium', 'tickets.urgency_high'];
    this.translate.get(keys)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.urgencyOptions = keys.map((key, index) => ({id: index + 1, name: r[key]}));
      });
  }

  getTickets(append = false) {
    this.isLoading = true;
    this.ticketService.getTickets(this.buildQueryString())
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const result = (r.data?.result ?? []) as ICoachTicket[];
        const meta = r.data?.meta;

        this.tickets = append ? [...this.tickets, ...result] : result;
        this.hasMore = meta ? (this.tickets.length < meta.total) : false;
      });
  }

  /** The header count ignores the active filters, so it always reports the real backlog. */
  getWaitingCount() {
    this.ticketService.getTickets(`?page=1&size=1&filter[status]=${ECoachTicketStatus.WaitingReply}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.waitingReplyCount = r.data?.meta?.total ?? 0;
      });
  }

  loadMore() {
    this.page += 1;
    this.getTickets(true);
  }

  applyFilters() {
    this.page = 1;
    this.openTicketId = undefined;
    this.getTickets();
  }

  setStatusFilter(status?: ECoachTicketStatus) {
    this.statusFilter = status;
    this.applyFilters();
  }

  setUrgencyFilter(urgency?: number) {
    this.urgencyFilter = urgency as ECoachTicketUrgency;
    this.applyFilters();
  }

  toggleTagFilter(tagId: number) {
    this.tagFilterIds = this.isTagFiltered(tagId)
      ? this.tagFilterIds.filter(id => id !== tagId)
      : [...this.tagFilterIds, tagId];

    this.applyFilters();
  }

  isTagFiltered(tagId: number): boolean {
    return this.tagFilterIds.includes(tagId);
  }

  clearTagFilter() {
    if (!this.tagFilterIds.length) {
      return;
    }

    this.tagFilterIds = [];
    this.applyFilters();
  }

  // region New ticket tags
  selectedTagIds(): number[] {
    return this.fg.get('tagIds')?.value ?? [];
  }

  isTagSelected(tagId: number): boolean {
    return this.selectedTagIds().includes(tagId);
  }

  toggleTag(tagId: number) {
    const selected = this.selectedTagIds();
    const next = selected.includes(tagId) ? selected.filter(id => id !== tagId) : [...selected, tagId];

    this.fg.patchValue({tagIds: next});
  }
  // endregion

  /** The labels on a ticket row; the API sends them as link rows. */
  ticketTags(ticket: ICoachTicket): ICoachTicketTag[] {
    return (ticket.tagItems ?? [])
      .map(item => item.tag)
      .filter((tag): tag is ICoachTicketTag => !!tag);
  }

  toggleForm() {
    this.formIsOpen = !this.formIsOpen;
    this.isSubmitted = false;
    if (!this.formIsOpen) {
      this.fg.reset({urgency: ECoachTicketUrgency.Medium, tagIds: []});
    }
  }

  createTicket() {
    this.isSubmitted = true;
    if (this.fg.invalid) {
      return;
    }

    this.createIsLoading = true;
    this.ticketService.createTicket(this.fg.value)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.createIsLoading = false))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('tickets.created'));
        this.toggleForm();
        this.page = 1;
        this.getTickets();
        this.getWaitingCount();
      });
  }

  toggleTicket(ticket: ICoachTicket) {
    if (this.openTicketId === ticket.id) {
      this.openTicketId = undefined;
      this.openTicket = undefined;
      return;
    }

    this.openTicketId = ticket.id;
    this.openTicket = undefined;
    this.replyIsSubmitted = false;
    this.fgReply.reset();

    this.detailIsLoading = true;
    this.ticketService.getTicket(ticket.id)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.detailIsLoading = false))
      .subscribe(r => {
        this.openTicket = r.data as ICoachTicket;
      });
  }

  sendReply() {
    this.replyIsSubmitted = true;
    if (this.fgReply.invalid || !this.openTicket) {
      return;
    }

    this.replyIsLoading = true;
    this.ticketService.addMessage(this.openTicket.id, this.fgReply.value.body)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.replyIsLoading = false))
      .subscribe(() => {
        this.replyIsSubmitted = false;
        this.fgReply.reset();
        this.reloadOpenTicket();
        this.getTickets();
        this.getWaitingCount();
      });
  }

  closeTicket(ticket: ICoachTicket) {
    this.ticketService.closeTicket(ticket.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('tickets.closed_success'));
        this.reloadOpenTicket();
        this.getTickets();
        this.getWaitingCount();
      });
  }

  lastMessage(ticket: ICoachTicket): ICoachTicketMessage | undefined {
    return ticket.messages?.[ticket.messages.length - 1];
  }

  private reloadOpenTicket() {
    if (!this.openTicket) {
      return;
    }

    this.ticketService.getTicket(this.openTicket.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.openTicket = r.data as ICoachTicket;
      });
  }

  private buildQueryString(): string {
    const parts = [`page=${this.page}`, `size=${PAGE_SIZE}`];

    if (this.statusFilter) {
      parts.push(`filter[status]=${this.statusFilter}`);
    }
    if (this.urgencyFilter) {
      parts.push(`filter[urgency]=${this.urgencyFilter}`);
    }
    if (this.tagFilterIds.length) {
      parts.push(`filter[tagIds]=${this.tagFilterIds.join(',')}`);
    }
    if (this.searchText?.trim()) {
      parts.push(`search=${encodeURIComponent(this.searchText.trim())}`);
    }

    return `?${parts.join('&')}`;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
